export const cleanTechnicalSuffix = (
  name: string | undefined | null
): string => {
  if (!name) return ''

  return name
    .replace(/(?<!&)\s?\bP\b\s?(?!\s*&)/g, ' ')
    .trim()
    .replace(/\s{2,}/g, ' ')
}

export const cleanFilterItems = <T extends { description?: string | null }>(
  items: readonly T[] | undefined
): T[] => {
  return (
    (items?.map((item) => ({
      ...item,
      description: cleanTechnicalSuffix(item.description),
    })) as T[]) || []
  )
}

const toChosenOptionValue = (option: unknown): string | null => {
  if (option === null || option === undefined) return null
  if (typeof option === 'object') {
    const { value } = option as { value?: unknown }
    return value === null || value === undefined ? null : String(value)
  }
  return String(option)
}

const normalizeChosenOptions = (options: unknown): string[] | undefined => {
  if (!Array.isArray(options)) return undefined
  return options
    .map(toChosenOptionValue)
    .filter((value): value is string => value !== null)
}

/**
 * `chosenOptions[].isRelevant` (п.11 записки) теряется при схлопывании
 * объектов в плоский `string[]` — вытаскиваем значения с `isRelevant: false`
 * отдельно, чтобы UI мог показать их серыми с тултипом.
 */
const extractIrrelevantOptionValues = (
  options: unknown
): string[] | undefined => {
  if (!Array.isArray(options)) return undefined

  const irrelevant = options
    .filter(
      (option): option is { value: unknown; isRelevant: false } =>
        typeof option === 'object' &&
        option !== null &&
        (option as { isRelevant?: unknown }).isRelevant === false
    )
    .map((option) => toChosenOptionValue(option))
    .filter((value): value is string => value !== null)

  return irrelevant.length > 0 ? irrelevant : undefined
}

type CleanableCharacteristic = {
  name?: string | null
  options?: unknown[]
  chosenOptions?: unknown
}

export const cleanCharacteristics = <T extends CleanableCharacteristic>(
  chars: readonly T[] | undefined
): Array<
  Omit<T, 'chosenOptions' | 'options'> & {
    options: NonNullable<T['options']>
    chosenOptions?: string[]
    irrelevantOptionValues?: string[]
  }
> => {
  return (
    chars?.map((char) => {
      const irrelevantOptionValues = extractIrrelevantOptionValues(
        char.chosenOptions
      )

      return {
        ...char,
        name: cleanTechnicalSuffix(char.name),
        options: (char.options || []) as NonNullable<T['options']>,
        ...(char.chosenOptions !== undefined && {
          chosenOptions: normalizeChosenOptions(char.chosenOptions) ?? [],
        }),
        ...(irrelevantOptionValues !== undefined && { irrelevantOptionValues }),
      }
    }) || []
  )
}

type CategorizedProduct = {
  nomenclatureDto: { categoryDto?: { description?: string | null } | null }
}

export const cleanProductsContent = <T extends CategorizedProduct>(
  products: readonly T[]
): T[] => {
  return products.map((p) => ({
    ...p,
    nomenclatureDto: {
      ...p.nomenclatureDto,
      categoryDto: p.nomenclatureDto.categoryDto
        ? {
            ...p.nomenclatureDto.categoryDto,
            description: cleanTechnicalSuffix(
              p.nomenclatureDto.categoryDto.description
            ),
          }
        : null,
    },
  }))
}

type Described = { description?: string | null }

type CleanableTreeNode = {
  category?: Described
  childCategories?: CleanableTreeNode[]
  nomenclatureGroupDto?: Described
  childGroups?: CleanableTreeNode[]
}

const cleanTreeNodes = (
  nodes: readonly CleanableTreeNode[]
): CleanableTreeNode[] =>
  nodes.map((node) => {
    const newNode: CleanableTreeNode = { ...node }

    if ('category' in node) {
      newNode.category = {
        ...node.category,
        description: cleanTechnicalSuffix(node.category?.description),
      }
      if (node.childCategories) {
        newNode.childCategories = cleanTreeNodes(node.childCategories)
      }
    } else if ('nomenclatureGroupDto' in node) {
      newNode.nomenclatureGroupDto = {
        ...node.nomenclatureGroupDto,
        description: cleanTechnicalSuffix(
          node.nomenclatureGroupDto?.description
        ),
      }
      if (node.childGroups) {
        newNode.childGroups = cleanTreeNodes(node.childGroups)
      }
    }

    return newNode
  })

export const cleanCategoryTree = <T extends object>(nodes: readonly T[]): T[] =>
  cleanTreeNodes(nodes as readonly CleanableTreeNode[]) as T[]

export const CLEANABLE_NAME_KEYS = new Set(['description', 'name', 'chrcName'])

export const cleanObject = <T>(obj: T, key?: string): T => {
  if (typeof obj === 'string') {
    return (
      key && CLEANABLE_NAME_KEYS.has(key) ? cleanTechnicalSuffix(obj) : obj
    ) as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => cleanObject(item, key)) as T
  }

  if (obj && typeof obj === 'object') {
    const cleaned: Record<string, unknown> = {}
    for (const [objectKey, value] of Object.entries(obj)) {
      cleaned[objectKey] = cleanObject(value, objectKey)
    }
    return cleaned as T
  }

  return obj
}
